import { Typography } from "@/components/atoms";
import { ClientBookingQuote } from "@/redux/apis/clientBooking/types";
import { clsx, formatCurrency } from "@/utils";
import { useBookingViewOnly } from "../BookingViewOnlyContext";
import { PriceSummary, PriceSummaryRow } from "../confirmation/PriceSummary";
import { JourneyCostOverride } from "./JourneyCostOverride";

/** The missing-field label the fixed-rate price gate contributes (bookingPayload.getMissingBookingFields). */
const FIXED_PRICE_FIELD = "Fixed price";

/** Format a dollar float (no cents conversion) as AUD — shared so the confirmation snapshot matches the summary exactly. */
export const fmt = (value: number) => formatCurrency(value, 2, "AUD", { skipDecimalConversion: true });

/** "a, b and c" — for the missing-fields hints here and under the Update Booking CTA. */
export const listify = (items: string[]) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * Rows the quote provides: journey cost + extras, then the itemized fees
 * (Service Fee, Credit Card Processing Fee) as their own divider-separated
 * group per Figma. processing_fees is absent until the backend change deploys,
 * so the fee group degrades away cleanly.
 */
export const toGroups = (quote: ClientBookingQuote): PriceSummaryRow[][] => {
  const rows: PriceSummaryRow[] = [{ label: "Journey Cost", value: fmt(quote.vehicles.cost) }];

  const extras = quote.vehicles.components ?? [];
  if (extras.length > 0) {
    rows.push({ label: "Extras" });
    extras.forEach((extra) => rows.push({ label: extra.name, value: `+ ${fmt(extra.cost)}`, indent: true }));
  }

  const fees = (quote.processing_fees?.components ?? []).map((fee) => ({ label: fee.name, value: `+ ${fmt(fee.cost)}` }));

  return fees.length > 0 ? [rows, fees] : [rows];
};

interface QuotePriceSummaryProps {
  quote?: ClientBookingQuote;
  isQuotable: boolean;
  isLoading: boolean;
  missingFields: string[];
  /**
   * Operator OBO only: reveals the Journey Cost override control (set/reset a fixed trip price for any
   * transfer type). Off for self-service clients, guests, and EA/assistant bookings.
   */
  canOverridePrice?: boolean;
  className?: string;
}

/** Fold the operator's Journey Cost override control into the first (Journey Cost) row's value cell. */
const withOverride = (groups: PriceSummaryRow[][], calculatedValue?: number): PriceSummaryRow[][] =>
  groups.map((group, groupIndex) =>
    group.map((row, rowIndex) =>
      groupIndex === 0 && rowIndex === 0
        ? { ...row, value: undefined, node: <JourneyCostOverride calculatedValue={calculatedValue} /> }
        : row
    )
  );

/**
 * Price Summary section of the priced Booking Summary. Live quote rows when the
 * draft is quotable; skeleton while quoting; a muted "what's left" hint otherwise.
 *
 * For an operator booking on behalf of a client (`canOverridePrice`), the Journey Cost row carries an
 * inline override control — including an "enter a price" state for a fixed-rate transfer type that has
 * no auto-quote yet (its only remaining blocker being the price itself).
 */
export const QuotePriceSummary = ({ quote, isQuotable, isLoading, missingFields, canOverridePrice, className }: QuotePriceSummaryProps) => {
  const viewOnly = useBookingViewOnly();

  // The operator can hide pricing from an invoice client — the backend nulls every cost/total and sets
  // is_pricing_available=false. Match the legacy sheet: omit the whole price summary rather than render
  // a misleading $0 for every row. Operators booking OBO always get is_pricing_available=true (they may
  // still set the price), so this only ever hides for the actual client. (undefined = older backend =
  // allowed, so gate strictly on `=== false`.)
  if (quote?.is_pricing_available === false) return null;

  if (isQuotable && quote && !isLoading) {
    const groups = canOverridePrice ? withOverride(toGroups(quote), quote.vehicles.cost) : toGroups(quote);
    return <PriceSummary groups={groups} total={fmt(Number(quote.total))} className={className} />;
  }

  // View-only with no stored price (e.g. a no-cost closed booking): there's nothing to show and no
  // action to prompt — omit the section entirely rather than showing a "complete X to see pricing" hint.
  if (viewOnly) return null;

  // Operator, fixed-rate transfer type awaiting its first price: there's no auto-quote to override, so
  // surface the override control in "enter a price" mode (once set, the draft quotes and the branch
  // above takes over). Any other still-missing fields are noted below so the operator knows what's left.
  if (canOverridePrice && !isLoading && missingFields.includes(FIXED_PRICE_FIELD)) {
    const others = missingFields.filter((field) => field !== FIXED_PRICE_FIELD);
    return (
      <div className={clsx("flex flex-col", className)}>
        <Typography variant="title">Price Summary</Typography>
        <div className="mt-2 flex items-center justify-between gap-3">
          <Typography variant="paragraph" className="min-w-0 text-neutral-dark-gray">
            Journey Cost
          </Typography>
          <div className="shrink-0">
            <JourneyCostOverride />
          </div>
        </div>
        {others.length > 0 && (
          <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
            {`Complete ${listify(others.map((field) => field.toLowerCase()))} to see the full price`}
          </Typography>
        )}
      </div>
    );
  }

  // Loading: show ONLY the skeleton — no "Price Summary" heading. A hidden-pricing client's quote
  // resolves to is_pricing_available=false and the whole section is then removed; rendering the heading
  // here first makes it flash in and vanish. A title-shaped pulse bar keeps the skeleton reading as the
  // price block without committing a real header (which appears with the rows once the quote lands).
  if (isLoading) {
    return (
      <div className={clsx("flex animate-pulse flex-col gap-2", className)} role="status" aria-label="Loading price">
        <div className="h-6 w-1/3 rounded-2xl bg-neutral-gray" />
        <div className="h-4 rounded-2xl bg-neutral-gray" />
        <div className="h-4 rounded-2xl bg-neutral-gray" />
        <div className="h-6 w-1/2 self-end rounded-2xl bg-neutral-gray" />
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-col", className)}>
      <Typography variant="title">Price Summary</Typography>
      <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
        {missingFields.length > 0
          ? `Complete ${listify(missingFields.map((field) => field.toLowerCase()))} to see pricing`
          : "Pricing is unavailable right now"}
      </Typography>
    </div>
  );
};
